
import { Component, Inject, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { FolderService } from '../../../Core/services/folder.service';
import { TagsService } from '../../../../Common/Tags/services/tags.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { FilterService } from '../../../../Common/Filter/services/abstract-filter.service';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { TagGroupsService } from '../../../../Common/Tags/services/tag-groups.service';
import { MediaSubscriptionService } from '../../../Core/services/media-subscription.service';
import { SourceGroupService } from '../../../../Common/Sources/services/source-group.service';
import { SourceService } from '../../../../Common/Sources/services/source.service';
import { ThemeService } from '../../../../Social/Intelligence/services/theme.service';
import { AgentService } from '../../../../Social/Intelligence/services/agent.service';
import { MentionSearchService } from '../../../Core/services/mention-search.service';
import { TopicsService } from '../../../Core/services/topics.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { BriefingsService } from '../../../Briefings/services/briefings.service';
import { MonitoringSourcePickerFieldWithFacetComponent } from './monitoring-source-picker-field-with-facet.component';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { CuratedMentionListDisplayElementModel } from '@Monitor/Briefings/models/curated-mention-list-display-element.model';
import { Mention } from '@Common/Mentions/models/mention';
import { MentionModel } from '@api/mentions/model/mention.model';
import { MonitoringSeriesTypeEnum } from '@Monitor/Shared/enums/monitoring-series-type.enum';
import { MentionSearchCriteriaModel } from '@Monitor/Shared/models/mention-search-criteria.model';
import { MentionSearchQueryModel } from '@Monitor/Shared/models/mention-search-query.model';
import { mentionSourceApiTypesEnumToMonitoringSeriesTypeEnum } from '@api/mentions/enum/mention-source-api-types.enum';
import { CurrencyConversionService } from '@Core/Users/services/currency-conversion.service';
import { CachedDateService } from '../../../../Common/MonitorShared/services/cached-date.service';

@Component({
    selector: 'agpr-briefing-source-picker-field',
    templateUrl: './monitoring-source-picker-field.component.html',
    styleUrls: ['./monitoring-source-picker-field.component.scss'],
    providers: [
        {
            provide: FilterService,
            useExisting: MentionSearchService
        }
    ],
    standalone: false
})
export class BriefingSourcePickerFieldComponent
    extends MonitoringSourcePickerFieldWithFacetComponent implements OnInit, OnDestroy, OnChanges {

    readonly messageSeverityEnum = messageSeverityEnum;
    allCuratedMentionsCount: number;

    initialLoad: boolean = false;
    usingCuratedMentions: boolean = false;
    ignoreInitialized: boolean = false;

    constructor(
        public topicsService: TopicsService,
        public monitorConfigService: MonitorConfigService,
        public folderService: FolderService,
        public tagsService: TagsService,
        public tagGroupsService: TagGroupsService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public sourceGroupService: SourceGroupService,
        public sourceService: SourceService,
        public themeService: ThemeService,
        public agentService: AgentService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public mentionSearchService: MentionSearchService,
        public localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService,
        private briefingsService: BriefingsService,
        private currencyConversionService: CurrencyConversionService,
        public cachedDateService: CachedDateService
    ) {
        super(
            topicsService,
            monitorConfigService,
            folderService,
            tagsService,
            tagGroupsService,
            mediaSubscriptionService,
            sourceGroupService,
            sourceService,
            themeService,
            agentService,
            appBaseHref,
            mentionSearchService,
            localizationService,
            currentUserStateService,
            cachedDateService
        );
    }

    ngOnInit() {
        // Initialize "Coverage in Section" options
        this.mentionBlocksOptions = this.briefingsService.getAllMentionBlocks().map(
            block => new DropdownOptionModel(
                (block.displayElement as CuratedMentionListDisplayElementModel)?.title,
                (block.displayElement as CuratedMentionListDisplayElementModel)
            )
        );
        this.mentionBlocksLoading = false;

        this.setupSubscriptions();
        this.initializeSelectedDataSource();
    }

    setupSubscriptions() {
        super.setupSubscriptions();

        this.subscriptions.push(
            (this.briefingsService as BriefingsService).getCurrentBriefingMentions().subscribe(mentions => {
                this.allCuratedMentionsCount = this.setMentionCount(mentions);
                if (this.sourceType === MonitoringSeriesTypeEnum.allCuratedCoverageInBrief) {
                    this.setNumberOfResults(this.allCuratedMentionsCount);
                    this.mentionSearchService.setMentionCount(this.allCuratedMentionsCount);
                    this.value.mentions = this.briefingsService.getAllCuratedMentions();
                    this.value.type = this.sourceType;
                }
            })
        );
    }

    setMentionCount(mentions: Mention[] | MentionModel[]): number {
        // curated block use MentionModel from API while all mentions use F/E Mention
        const ids = [];
        mentions.forEach(mention => {
            if (mention.canGenerateInsight) {
                if (mention.mentionId) {
                    ids.push(mention.mentionId);
                } else if (mention.id) {
                    ids.push(mention.id);
                } else if (mention.mercuryId) {
                    ids.push(mention.mercuryId);
                }
            }
        });

        return [...new Set(ids)].length;
    }

    /**
     * Selects a type of series
     */
    public async selectType(type: MonitoringSeriesTypeEnum): Promise<void> {
        if (type === MonitoringSeriesTypeEnum.allCuratedCoverageInBrief) {
            this.sourceType = type;
            this.selectedSource = null;
            this.resetFilters();

            this.value.type = this.sourceType;
            this.value.id = null;
            this.value.mentions = this.briefingsService.getAllCuratedMentions();

            this.setAccountFilters();
            await this.updateSearchCriteria();
            this.clearSearch();

            this.changes.emit(this.value);
            this.setNumberOfResults(this.allCuratedMentionsCount);
            this.mentionSearchService.setMentionCount(this.allCuratedMentionsCount);
        } else {
            super.selectType(type);
        }
    }

    /**
     * Selects the series data source
     */
    public async selectItem(item: DropdownOptionModel): Promise<void> {
        if (this.sourceType === MonitoringSeriesTypeEnum.coverageInSection) {
            this.usingCuratedMentions = false;

            this.clearSearch();

            if (!item) {
                this.value.id = null;
                this.value.dataSourceName = null;
                this.changes.emit(this.value);
                return;
            }

            this.value.id = item?.value?.backendDataSource?.id;
            this.value.referenceId = item?.value?.blockReferenceId;

            if (item?.value && item.value instanceof CuratedMentionListDisplayElementModel) {
                this.usingCuratedMentions = true;
                this.value.mentions = item?.value?.mentions;
                const numOfMentions = this.setMentionCount(item?.value?.mentions ?? []);
                this.setNumberOfResults(numOfMentions);
                this.mentionSearchService.setMentionCount(numOfMentions);
                delete this.value.sourceSearchCriteria;
            } else {
                this.usingCuratedMentions = false;
                this.setAutomaticSource(item?.value);
            }

            this.changes.emit(this.value);
        } else {
            super.selectItem(item);
        }
    }

    async setAutomaticSource(source) {
        let filterCriteria: MentionSearchCriteriaModel;
        let filterQuery: MentionSearchQueryModel;

        const sourceType = mentionSourceApiTypesEnumToMonitoringSeriesTypeEnum.get(source?.sourceType);
        if (source?.filterMentionSearchModel?.searchCriteria?.query?.dateQuery) {
            this.dateQuery = source?.filterMentionSearchModel.searchCriteria.query.dateQuery;
        }

        if (source?.filterMentionSearchModel?.searchCriteria) {
            filterCriteria = source?.filterMentionSearchModel?.searchCriteria;
            if (!source?.filterMentionSearchModel?.searchCriteria.query.isEmpty()) {
                filterQuery = source?.filterMentionSearchModel?.searchCriteria.query;

                // convert currency from USD to user currency as B.E currently only saves USD
                if (filterQuery?.ave?.min) {
                    filterQuery.ave.min = this.currencyConversionService.convertCurrencyWithoutISOCode(filterQuery?.ave?.min);
                }
                if (filterQuery?.ave?.max && filterQuery?.ave?.max !== Mention.maxAudienceReach) {
                    filterQuery.ave.max = this.currencyConversionService.convertCurrencyWithoutISOCode(filterQuery?.ave?.max);
                }
            }
        }

        await this.updateSearchCriteria(sourceType);
        if (source?.filterMentionSearchModel?.searchCriteria?.query?.dateQuery) {
            this.setSearchDateQuery(source?.filterMentionSearchModel?.searchCriteria.query.dateQuery);
        }

        this.mentionSearchService.setSortField(source?.filterMentionSearchModel?.searchCriteria.sortField);
        this.mentionSearchService.setSortOrder(source?.filterMentionSearchModel?.searchCriteria.sortOrder);

        this.search();
    }
}
